import React, { useState } from "react";
import { Button } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import EmailDetailsSection from "./EmailDetailsSection";
import RecipientSection from "./RecipientSection";
import EmailContentSection from "./EmailContentSection";
import EmailTemplateSection from "./EmailTemplateSection";

// Form state type
type CreateEmailForm = {
  emailDetails: {
    subject: string;
    preheaderText: string;
    fromName: string;
    fromEmail: string;
    replyToEmail: string;
    emailType: string;
  };
  recipients: {
    sendTo: string[];
    includeMembers: boolean;
    includeNonMembers: boolean;
    includeEblastSubscribers: boolean;
    specificGroups: string[];
  };
  content: {
    emailContent: string;
    attachments: File[];
  };
  template: {
    selectedTemplate: string;
    headerImage: string;
    footerText: string;
    socialLinks: {
      facebook: string;
      twitter: string;
      instagram: string;
      linkedin: string;
    };
  };
};

// Initial form state
const initialFormState: CreateEmailForm = {
  emailDetails: {
    subject: "",
    preheaderText: "",
    fromName: "Chamber of Commerce",
    fromEmail: "",
    replyToEmail: "",
    emailType: "",
  },
  recipients: {
    sendTo: [],
    includeMembers: true,
    includeNonMembers: false,
    includeEblastSubscribers: true,
    specificGroups: [],
  },
  content: {
    emailContent: "",
    attachments: [],
  },
  template: {
    selectedTemplate: "standard",
    headerImage: "",
    footerText: "© 2023 Chamber of Commerce. All rights reserved.",
    socialLinks: {
      facebook: "",
      twitter: "",
      instagram: "",
      linkedin: "",
    },
  },
};

const emailTypes = [
  { value: "newsletter", label: "Chamber Newsletter" },
  { value: "press_release", label: "Chamber Press Release" },
  { value: "bulletin", label: "Chamber Bulletin" },
  { value: "eblast", label: "Chamber E-Blast" },
];

const CreateEmail = () => {
  const [formData, setFormData] = useState<CreateEmailForm>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email details handler
  const handleEmailDetailsChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.emailDetails;
    value: string;
  }) => {
    setFormData({
      ...formData,
      emailDetails: {
        ...formData.emailDetails,
        [field]: value,
      },
    });
  };

  // Recipients handler
  const handleRecipientsChange = ({
    field,
    value,
  }: {
    field: keyof Omit<typeof formData.recipients, "sendTo" | "specificGroups">;
    value: boolean;
  }) => {
    setFormData({
      ...formData,
      recipients: {
        ...formData.recipients,
        [field]: value,
      },
    });
  };

  const handleGroupsChange = ({ groups }: { groups: string[] }) => {
    setFormData({
      ...formData,
      recipients: {
        ...formData.recipients,
        specificGroups: groups,
      },
    });
  };

  // Content handler
  const handleContentChange = ({ content }: { content: string }) => {
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        emailContent: content,
      },
    });
  };

  const handleAddAttachment = ({ file }: { file: File }) => {
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        attachments: [...formData.content.attachments, file],
      },
    });
  };

  const handleRemoveAttachment = ({ index }: { index: number }) => {
    const newAttachments = [...formData.content.attachments];
    newAttachments.splice(index, 1);

    setFormData({
      ...formData,
      content: {
        ...formData.content,
        attachments: newAttachments,
      },
    });
  };

  // Template handler
  const handleTemplateChange = ({
    field,
    value,
  }: {
    field: keyof Omit<typeof formData.template, "socialLinks">;
    value: string;
  }) => {
    setFormData({
      ...formData,
      template: {
        ...formData.template,
        [field]: value,
      },
    });
  };

  const handleSocialLinksChange = ({
    platform,
    value,
  }: {
    platform: keyof typeof formData.template.socialLinks;
    value: string;
  }) => {
    setFormData({
      ...formData,
      template: {
        ...formData.template,
        socialLinks: {
          ...formData.template.socialLinks,
          [platform]: value,
        },
      },
    });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Basic validation
    if (!formData.emailDetails.subject.trim()) {
      newErrors.subject = "Email subject is required";
    }

    if (!formData.emailDetails.fromEmail.trim()) {
      newErrors.fromEmail = "From email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.emailDetails.fromEmail)) {
      newErrors.fromEmail = "Please enter a valid email address";
    }

    if (!formData.emailDetails.emailType) {
      newErrors.emailType = "Please select an email type";
    }

    if (
      formData.recipients.specificGroups.length === 0 &&
      !formData.recipients.includeMembers &&
      !formData.recipients.includeNonMembers &&
      !formData.recipients.includeEblastSubscribers
    ) {
      newErrors.recipients = "Please select at least one recipient group";
    }

    if (!formData.content.emailContent.trim()) {
      newErrors.content = "Email content is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Here you would normally send the data to your API
    console.log("Submitting email data:", formData);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      alert("Email created successfully!");
    }, 1500);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="max-w-4xl mx-auto">
        <EmailDetailsSection
          emailDetails={formData.emailDetails}
          onChange={handleEmailDetailsChange}
          emailTypes={emailTypes}
          errors={errors}
        />

        <RecipientSection
          recipients={formData.recipients}
          onChange={handleRecipientsChange}
          onGroupsChange={handleGroupsChange}
          errors={errors}
        />

        <EmailTemplateSection
          template={formData.template}
          onChange={handleTemplateChange}
          onSocialLinksChange={handleSocialLinksChange}
          errors={errors}
        />

        <EmailContentSection
          content={formData.content}
          onContentChange={handleContentChange}
          onAddAttachment={handleAddAttachment}
          onRemoveAttachment={handleRemoveAttachment}
          errors={errors}
        />

        <div className="mt-8 flex justify-center">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            disabled={isSubmitting}
            sx={{
              backgroundColor: "#E1A731",
              "&:hover": {
                backgroundColor: "#c78f1e",
              },
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              px: 4,
              py: 1.5,
            }}
          >
            {isSubmitting ? "Sending..." : "Send Email"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateEmail;
